import React, { useMemo, useState, useContext } from 'react';
import { Detail as DetailComponent, Confirm, useDrawer, SplitButton } from 'src/mtska-frontend-app-component';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import { useTranslation } from 'react-i18next';
import { AppContext } from 'src/mtska-frontend-app-scaffolding';
import { useLoadOne } from "src/mtska-frontend-data-provider/hooks/useLoadOne.hooks";

const dataProviderName = 'persons/employment';

const toRelationshipIds = (primary, fallback) => {
    const raw = primary ?? fallback;
    if (raw == null) {
        return [];
    }
    const arr = Array.isArray(raw) ? raw : [raw];
    return arr
        .map((item) => (typeof item === 'object' && item != null ? (item._id ?? item.id) : item))
        .filter(Boolean);
};

/** Map id-only pivot fields for async relationship widgets (dataProviderName in detailConfig). */
const hydrateRelationshipIds = (data) => {
    if (!data || typeof data !== 'object') {
        return data;
    }
    return {
        ...data,
        managedLegalEntities: toRelationshipIds(data.managedLegalEntities, data.managed_legal_entity_ids),
        secondaryAreas: toRelationshipIds(data.secondaryAreas, data.secondary_area_ids),
        sellerAgentOfProposals: toRelationshipIds(data.sellerAgentOfProposals, data.seller_agent_of_proposal_ids),
        buyerAgentOfProposals: toRelationshipIds(data.buyerAgentOfProposals, data.buyer_agent_of_proposal_ids),
    };
};

const Detail = (params) => {
    const { t } = useTranslation();
    const { close: closeDrawer } = useDrawer();
    const { userCapabilities, existingModules } = useContext(AppContext);

    const { item, save, loadOne, destroy, errorMessage, errors, saving, reloadCounter } = useDataProvider(dataProviderName, params.multipart);

    const { modelName, detailConfig } = existingModules[dataProviderName];

    useLoadOne({ loadOne, params, reloadCounter });

    const displayValues = useMemo(
        () => hydrateRelationshipIds(item?.data),
        [item?.data]
    );

    const [editing, setEditing] = useState(false);

    const handleSave = (data) => {
        save({ ...data, filters: params.filters }).then((data) => params.handleClose(params.uid, data.item.data)).catch(() => console.error('catch handleSave'));
    };

    const [confirmDelete, setConfirmDelete] = useState();
    const handleDelete = (id) => {
        setConfirmDelete(
            <Confirm
                oklabel={t("Delete")}
                okcallable={() => {
                    destroy && destroy(id);
                    closeDrawer();
                }}
                kolabel={t("Cancel")}
                kocallable={() => { setConfirmDelete(null); }}
                title={t("Item elimination")}>
                {t("Are you sure to remove this element?")}
            </Confirm>
        );
    };

    return (
        <>
            <DetailComponent
                disableDetailConfigOverride={true}
                values={displayValues}
                modelName={modelName}
                detailConfig={detailConfig}
                dataProviderName={dataProviderName}
                onEditing={setEditing}
                save={handleSave}
                saving={saving}
                loading={!displayValues}
                errors={errors}
                errorMessage={errorMessage}
                canSave={!displayValues?.terminated_at && userCapabilities && userCapabilities[dataProviderName + '.edit']}
                readonly={!!displayValues?.terminated_at}
                title={(displayValues?.role) ? (displayValues?.person?.firstname + ' ' + displayValues?.person?.lastname + ' | ' + displayValues?.role + ' | ' + displayValues?.legalEntity?.name) : t('New Employment')}
                rightButtons={displayValues?.terminated_at ?? [
                    <SplitButton
                        actions={[
                            {
                                disabled: userCapabilities && !userCapabilities[dataProviderName + '.edit'],
                                icon: "faTrash",
                                label: t('Delete'),
                                onClick: () => { handleDelete(displayValues.id); }
                            }
                        ]}
                    />
                ]}
                {...params}
            />
            {confirmDelete}
        </>
    );
};

export default Detail;
